import { Trans, useTranslation } from 'react-i18next';
import styles from './FilterSummary.module.scss';
import Skeleton from 'react-loading-skeleton';
import { VdsButton } from '@volt/volt-extended-components-react';
import { useAppDispatch, useAppSelector } from '@customer/store/hooks';
import { useCallback } from 'react';
import { clearFiltersSubset } from '@customer/store/slices/filtersSlice';
import { FilterGroup } from '@customer/filters/types';
import { useHasSavedFilters } from '@customer/filters/useHasFilters';
import { ModelName } from '@customer/store/types';

export interface FilterSummaryProps {
  transKey: string;
  filteredTransKey?: string;
  countFilter?: number;
  countAll?: number;
  filterGroup?: FilterGroup;
  modelName?: ModelName;
}

export function FilterSummary({
  transKey,
  filteredTransKey,
  countFilter,
  countAll,
  filterGroup,
  modelName,
}: FilterSummaryProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { siteId, dateTimeFormat } = useAppSelector(state => state.auth);

  const hasFilters = useHasSavedFilters(filterGroup);

  const isFiltered = typeof filterGroup !== 'undefined' && hasFilters;
  const isDataLoaded = typeof countAll === 'number';
  const isVerbose =
    typeof filteredTransKey !== 'undefined' &&
    typeof countFilter === 'number' &&
    countFilter !== countAll;

  const onClearFilters = useCallback(() => {
    if (isFiltered && typeof siteId !== 'undefined') {
      dispatch(clearFiltersSubset({ filterGroup, siteId }));
    }
  }, [dispatch, filterGroup, isFiltered, siteId]);

  return (
    <div className={styles.filterSummary}>
      <div className={styles.filterSummary__text}>
        {isDataLoaded ? (
          isVerbose ? (
            <Trans
              i18nKey={filteredTransKey}
              values={{
                countAll, // Keep countAll param for backward compatibility with specific translations.
                count: countAll, // Use the default 'count' param to get i18n default pluralisation logic working.
                countFilter,
                modelName, // This param only gets used if the translation implements it.
              }}
              components={{ b: <strong /> }}
              tOptions={{
                formatParams: {
                  count: { lng: dateTimeFormat },
                  countAll: { lng: dateTimeFormat },
                  countFilter: { lng: dateTimeFormat },
                },
              }}
            />
          ) : (
            <Trans
              i18nKey={transKey}
              values={{ count: countAll, modelName }}
              components={{ b: <strong /> }}
              tOptions={{ formatParams: { count: { lng: dateTimeFormat } } }}
            />
          )
        ) : (
          <Skeleton width={150} height="22px" />
        )}
      </div>

      {isFiltered && (
        <VdsButton onClick={onClearFilters} variant="ghost" size="medium">
          {t('ClearFilters')}
        </VdsButton>
      )}
    </div>
  );
}

export default FilterSummary;
